/**
 * Dairy: lactations, milk weights and test days, fresh-cow checks, dry-off, mastitis and what was
 * done with the milk. A calving recorded by breeding starts a lactation for a cow with dairy
 * records; beef cows never get a dairy facet. Milk holds belong to health and are only read here.
 */
import { defineEvent, defineFacet, defineModule, f, msg, daysBetween, type Badge, type Fact, type Formatters, type SubjectRef } from "../../contract";
import {
  DAIRY_EVENT_TYPES, FRESH_BADGE_DAYS, activeMastitis, buildDairy, freshFlags, liveDim, milkOutActive, toFacet,
  type DairyFacet, type DryOffData, type FreshCheckData, type LactationStartData, type MastitisData, type MilkDispositionData, type MilkWeightData, type TestDayData,
} from "./shared/lactation";

export type { DairyFacet } from "./shared/lactation";

const COW = { role: "cow", kind: "animal" as const, label: "Cow", required: true };
const COWS = { role: "cow", kind: "animal" as const, label: "Cows", required: true, many: true };
const MILK_UNITS = ["lb", "kg", "l"] as const;

const cowsLabel = (subjects: SubjectRef[], x: Formatters) => x.list(subjects.filter((s) => s.role === "cow").map((s) => s.label ?? x.label(s.id)));

export const QUARTER_LABELS = { lf: "Left front", rf: "Right front", lr: "Left rear", rr: "Right rear" } as const;
export const START_CAUSES = { calving: "Calving", abortion: "Abortion", induced: "Induced lactation", bought_fresh: "Bought fresh", other: "Other" } as const;
export const KETONES = { negative: "Negative", trace: "Trace", small: "Low", moderate: "Moderate", large: "High" } as const;
export const APPETITE = { good: "Eating well", fair: "Eating some", poor: "Off feed" } as const;
export const FRESH_ACTIONS = { none: "Nothing needed", recheck: "Recheck tomorrow", treated: "Treated", vet: "Call the vet", hospital: "Moved to hospital pen" } as const;
export const DISCHARGE = { clear: "Clear or normal", cloudy: "Cloudy or flecked", pus: "Pus", fetid: "Fetid, watery" } as const;
export const DA_CHECK = { none: "No ping", left: "Ping on the left", right: "Ping on the right" } as const;
export const ATTITUDE = { bright: "Bright and alert", dull: "Dull", down: "Down" } as const;
export const SEVERITY = { mild: "Mild: abnormal milk", moderate: "Moderate: hard or swollen quarter", severe: "Severe: sick cow" } as const;
export const CMT = { negative: "Negative", trace: "Trace", one: "1", two: "2", three: "3" } as const;
export const DISPOSITIONS = { discarded: "Dumped", withheld: "Kept out of the tank", sold: "Back in the tank" } as const;
export const MILK_REASONS = { treatment: "Treatment withdrawal", mastitis: "Mastitis", fresh: "Fresh cow (colostrum)", high_scc: "High SCC", other: "Other" } as const;
const label = <T extends Record<string, string>>(map: T, value: string | undefined): string => (value && value in map ? map[value as keyof T] : value ?? "");

// ---------------------------------------------------------------- events

const lactationStart = defineEvent<LactationStartData>({
  type: "dairy.lactation_start",
  label: "Lactation start",
  description: "A cow started a new lactation without a calving record: bought fresh, after an abortion, induced, or an old start date.",
  icon: "milk",
  tone: "dairy",
  subjects: [COW],
  fields: {
    cause: f.enum("Started by", START_CAUSES, {
      agent: "Use calving only when the calving itself is not being recorded as breeding.calving.",
    }),
    lactation: f.int("Lactation number", { level: "more", min: 1, max: 20, help: "Leave it blank and Hondo counts from her records." }),
    notes: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const cause = e.data.cause && e.data.cause !== "calving" ? x.t(label(START_CAUSES, e.data.cause)) : null;
    const base = e.data.lactation ? x.t("Fresh, lactation {{number}}", { number: e.data.lactation }) : x.t("Fresh, new lactation");
    return cause ? `${base} · ${cause}` : base;
  },
  agent: {
    when: "A cow began a new lactation and no calving is being recorded: bought fresh, milked after an abortion, induced, or entering an old fresh date.",
    avoid: "When a cow or heifer calves, record breeding.calving only; Hondo starts her lactation from the calving. Not for drying off.",
    examples: ["0215 came in fresh from the Miller sale", "0188 aborted and we're milking her", "la 0301 llegó fresca, tercera lactancia"],
  },
});

const milkWeight = defineEvent<MilkWeightData>({
  type: "dairy.milk_weight",
  label: "Milk weight",
  description: "Milk from one cow, at one milking or for the whole day.",
  icon: "milk",
  tone: "dairy",
  subjects: [COW],
  fields: {
    milk: f.quantity("Milk", "weight", { required: true, units: MILK_UNITS, agent: "As said: pounds (lb), kilograms (kg) or liters (l). Never fill a weight nobody said." }),
    period: f.enum("Covers", { day: "Whole day", milking: "One milking" }, { required: true, agent: "milking for one session (AM, PM); day for a daily total" }),
    session: f.enum("Which milking", { am: "Morning milking", midday: "Midday milking", pm: "Evening milking" }, { level: "more" }),
    milkings: f.int("Milkings a day", { level: "more", min: 1, max: 6 }),
    source: f.enum("Measured by", { meter: "Parlor meter", robot: "Robot", bucket: "Bucket or scale", estimate: "Estimate" }, {
      level: "more", agent: "estimate when they guessed; never present a guess as a meter reading",
    }),
    notes: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const milk = x.quantity(e.data.milk);
    const text = e.data.period === "day" ? x.t("{{milk}} for the day", { milk })
      : e.data.session === "am" ? x.t("{{milk}} at the morning milking", { milk })
        : e.data.session === "midday" ? x.t("{{milk}} at the midday milking", { milk })
          : e.data.session === "pm" ? x.t("{{milk}} at the evening milking", { milk })
            : x.t("{{milk}} at one milking", { milk });
    return e.data.source === "estimate" ? x.t("{{text}} (estimate)", { text }) : text;
  },
  agent: {
    when: "A milk weight or meter reading for one cow, for one milking or a daily total.",
    avoid: "Not a lab or DHIA test with fat, protein or SCC (dairy.test_day). Do not record a guess as a meter reading; use source estimate.",
    examples: ["0142 gave 41 pounds this morning", "0118 milked 88 lb yesterday", "la 0131 dio 30 kilos hoy"],
  },
});

const testDay = defineEvent<TestDayData>({
  type: "dairy.test_day",
  label: "Test day",
  description: "Milk test results for one cow: test-day milk, fat, protein, somatic cell count and MUN.",
  icon: "lab",
  tone: "dairy",
  subjects: [COW],
  fields: {
    milk: f.quantity("Test-day milk", "weight", { units: MILK_UNITS }),
    fat_pct: f.number("Butterfat", { suffix: "%", min: 0, max: 15, step: 0.01 }),
    protein_pct: f.number("Protein", { suffix: "%", min: 0, max: 10, step: 0.01 }),
    scc: f.int("SCC", {
      suffix: "×1,000 cells/mL", min: 0, max: 99999,
      help: "In thousands of cells per mL, as labs report it: 250 means 250,000.",
      agent: "Thousands of cells per mL as labs report it: 250 means 250,000 cells/mL. Divide a full count like 250000 by 1,000. Leave out when not given.",
    }),
    mun: f.number("MUN", { level: "more", suffix: "mg/dL", min: 0, max: 60 }),
    source: f.enum("Tested by", { dhia: "DHIA or milk recording", lab: "Private lab", on_farm: "On-farm test" }, { level: "more" }),
    lab: f.text("Lab name", { level: "more" }),
    sample_id: f.text("Sample ID", { level: "more" }),
    dim_reported: f.int("Days in milk (from the lab)", { level: "more", min: 0, max: 1500 }),
    lactation_reported: f.int("Lactation (from the lab)", { level: "more", min: 1, max: 20 }),
    me305_reported: f.quantity("305ME (from the lab)", "weight", { level: "more", units: MILK_UNITS, help: "Only as reported by the lab. Hondo labels its own estimate separately." }),
    milkings: f.int("Milkings a day", { level: "more", min: 1, max: 6 }),
    notes: f.longText("Notes", { level: "more" }),
  },
  validate: (d) => (d.milk || d.fat_pct !== undefined || d.protein_pct !== undefined || d.scc !== undefined || d.mun !== undefined ? []
    : [{ path: "data.milk", message: "Add at least one test result." }]),
  summary: (e, x) => {
    const d = e.data;
    const parts = [
      d.milk ? x.quantity(d.milk) : null,
      d.fat_pct !== undefined ? x.t("fat {{value}}%", { value: d.fat_pct }) : null,
      d.protein_pct !== undefined ? x.t("protein {{value}}%", { value: d.protein_pct }) : null,
      d.scc !== undefined ? x.t("SCC {{value}}k", { value: d.scc }) : null,
      d.mun !== undefined ? x.t("MUN {{value}}", { value: d.mun }) : null,
    ].filter(Boolean) as string[];
    return x.t("Test day: {{results}}", { results: parts.join(", ") });
  },
  agent: {
    when: "Milk test results for a cow (DHIA, lab or on-farm): test-day milk, fat %, protein %, SCC, MUN. For a photographed test sheet, one item per cow row.",
    avoid: "Not a daily meter weight (dairy.milk_weight). Never fill fat, protein, SCC or MUN that the sheet or speaker doesn't give. Whole test-day files (CSV) are imported in Run dairy, Test days.",
    examples: ["test day results came in: 0142 80 lb, fat 4.1, protein 3.4, SCC 250", "0118 SCC 1200 on the October test", "prueba de la 0131: 70 libras, grasa 3.8"],
  },
});

const dryOff = defineEvent<DryOffData>({
  type: "dairy.dry_off",
  label: "Dry-off",
  description: "Cows actually dried off: milking stopped before calving.",
  icon: "moon",
  tone: "dairy",
  subjects: [COWS],
  fields: {
    treatment: f.text("Dry treatment", {
      help: "What was given, by name. Record the treatment in health too so withdrawal is tracked.",
      agent: "Product names as said, e.g. 'Orbenin-DC and Orbeseal'. Also propose the health treatment record when products were given.",
    }),
    teat_sealant: f.bool("Teat sealant", { level: "more" }),
    protocol: f.text("Protocol", { level: "more" }),
    planned_on: f.date("Planned dry-off date", { level: "more", help: "When it was planned. The record's date is when she was actually dried off." }),
    reason: f.enum("Why", { planned: "Planned before calving", low_milk: "Low production", health: "Health problem", other: "Other" }, { level: "more" }),
    notes: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const cows = cowsLabel(e.subjects, x);
    return e.data.treatment ? x.t("Dried off {{cows}} with {{treatment}}", { cows, treatment: e.data.treatment }) : x.t("Dried off {{cows}}", { cows });
  },
  agent: {
    when: "Cows were actually dried off (milking stopped).",
    avoid: "A plan to dry off later is a task, not a dry-off. Not a lactation start.",
    examples: ["dried off 0118 and 0131", "secamos la 0142 con Orbenin y sellador"],
  },
});

const freshCheck = defineEvent<FreshCheckData>({
  type: "dairy.fresh_check",
  label: "Fresh-cow check",
  description: "A check of a recently calved cow: temperature, ketones, appetite, discharge and what was done.",
  icon: "thermometer",
  tone: "dairy",
  subjects: [COW],
  fields: {
    temp: f.quantity("Temperature", "temperature", { units: ["F", "C"] }),
    ketones: f.enum("Ketones", KETONES),
    appetite: f.enum("Appetite", APPETITE),
    action: f.enum("What was done", FRESH_ACTIONS, {
      agent: "treated only when they say something was given; the treatment itself is a health record",
    }),
    discharge: f.enum("Uterine discharge", DISCHARGE, { level: "more" }),
    da: f.enum("DA check", DA_CHECK, { level: "more" }),
    attitude: f.enum("Attitude", ATTITUDE, { level: "more" }),
    bhb: f.number("Blood BHB", { level: "more", suffix: "mmol/L", min: 0, max: 10, step: 0.1 }),
    notes: f.longText("Notes", { level: "more" }),
  },
  validate: (d) => (d.temp || d.ketones || d.appetite || d.discharge || d.da || d.attitude || d.bhb !== undefined || d.action || d.notes ? []
    : [{ path: "data.temp", message: "Add at least one thing you checked." }]),
  effects: {
    followUps: (d, on, subjects) => {
      const cow = subjects.find((s) => s.role === "cow");
      return d.action === "vet" && on ? [{ key: "vet", title: msg("Call the vet about a fresh cow"), due_at: on, subject: cow ? { kind: "animal", id: cow.id } : undefined }] : [];
    },
  },
  summary: (e, x) => {
    const d = e.data;
    const parts = [
      d.temp ? x.quantity(d.temp) : null,
      d.ketones ? x.t("ketones {{level}}", { level: x.t(label(KETONES, d.ketones)).toLowerCase() }) : null,
      d.appetite ? x.t(label(APPETITE, d.appetite)).toLowerCase() : null,
      d.discharge && d.discharge !== "clear" ? x.t(label(DISCHARGE, d.discharge)).toLowerCase() : null,
      d.da && d.da !== "none" ? x.t(label(DA_CHECK, d.da)).toLowerCase() : null,
      d.attitude && d.attitude !== "bright" ? x.t(label(ATTITUDE, d.attitude)).toLowerCase() : null,
      d.bhb !== undefined ? x.t("BHB {{value}}", { value: d.bhb }) : null,
    ].filter(Boolean) as string[];
    const head = parts.length ? parts.join(", ") : x.t("Checked");
    return d.action && d.action !== "none" ? `${head} · ${x.t(label(FRESH_ACTIONS, d.action))}` : head;
  },
  agent: {
    when: "A fresh (recently calved) cow was checked: temperature, ketones, appetite, discharge, DA ping, and what was done about it.",
    avoid: "Drugs, calcium or drench given are health treatment records too; this only records the check and the action.",
    examples: ["0142 fresh check, temp 103.1, ketones moderate", "checked the fresh pen, 0155 off feed, ping on the left", "la 0160 con 39.8 de temperatura, comiendo bien"],
  },
});

const mastitis = defineEvent<MastitisData>({
  type: "dairy.mastitis",
  label: "Mastitis",
  description: "A mastitis case, recheck or cure: which quarters, how bad, CMT, culture and whether the milk is dumped.",
  icon: "water",
  tone: "health",
  subjects: [COW],
  fields: {
    quarters: f.multiEnum("Quarters", QUARTER_LABELS),
    severity: f.enum("How bad", SEVERITY),
    discard: f.bool("Milk dumped"),
    discard_until: f.date("Dump milk until", { help: "The last day her milk stays out of the tank, when known." }),
    stage: f.enum("Case", { new: "New case", recheck: "Recheck", cleared: "Cleared up" }, { level: "more", agent: "new unless they say it's a recheck or cleared up" }),
    cmt: f.enum("CMT", CMT, { level: "more" }),
    culture: f.enum("Culture", { not_sent: "Not cultured", pending: "Sent to the lab", back: "Result back" }, { level: "more" }),
    culture_result: f.text("Culture result", { level: "more" }),
    treatment: f.text("Treatment", { level: "more", help: "By name. Record the treatment in health too so withdrawal is tracked." }),
    recheck_on: f.date("Recheck on", { level: "more" }),
    notes: f.longText("Notes", { level: "more" }),
  },
  validate: (d) => (d.discard_until && d.discard === false ? [{ path: "data.discard_until", message: "Turn on Milk dumped to set a date." }] : []),
  effects: {
    followUps: (d, _on, subjects) => {
      const cow = subjects.find((s) => s.role === "cow");
      return d.recheck_on ? [{ key: "recheck", title: msg("Mastitis recheck"), due_at: d.recheck_on, subject: cow ? { kind: "animal", id: cow.id } : undefined }] : [];
    },
  },
  summary: (e, x) => {
    const d = e.data;
    const quarters = d.quarters?.length ? x.list(d.quarters.map((q) => x.t(QUARTER_LABELS[q]).toLowerCase())) : null;
    const head = d.stage === "cleared" ? x.t("Mastitis cleared up") : d.stage === "recheck" ? x.t("Mastitis recheck") : x.t("Mastitis");
    const parts = [
      quarters,
      d.severity ? x.t(d.severity === "mild" ? "mild" : d.severity === "moderate" ? "moderate" : "severe") : null,
      d.cmt ? x.t("CMT {{score}}", { score: x.t(label(CMT, d.cmt)).toLowerCase() }) : null,
      d.discard ? (d.discard_until ? x.t("milk dumped until {{date}}", { date: x.date(d.discard_until) }) : x.t("milk dumped")) : null,
    ].filter(Boolean);
    return parts.length ? `${head}: ${parts.join(", ")}` : head;
  },
  agent: {
    when: "Mastitis in a cow: a new case, a recheck or cleared up, with quarters, severity, CMT, culture and whether her milk is dumped.",
    avoid: "The drug given is a health treatment record too. Use dairy.milk_disposition for milk handling that isn't part of a mastitis case.",
    examples: ["milk from 0142 dumped, treating left rear quarter", "0118 mastitis right front, CMT 2, culture sent", "la 0155 tiene mastitis en la trasera izquierda"],
  },
});

const milkDisposition = defineEvent<MilkDispositionData>({
  type: "dairy.milk_disposition",
  label: "Milk handling",
  description: "What was done with a cow's milk: dumped, kept out of the tank, or back in the tank. Separate from health withdrawal holds.",
  icon: "milk",
  tone: "warn",
  subjects: [COWS],
  fields: {
    disposition: f.enum("Her milk", DISPOSITIONS, {
      required: true, agent: "withheld when kept out of the bulk tank but used, for example fed to calves",
    }),
    until: f.date("Until", { help: "The last day out of the tank, when known." }),
    reason: f.enum("Why", MILK_REASONS),
    amount: f.quantity("Amount of milk", "weight", { level: "more", units: MILK_UNITS }),
    notes: f.longText("Notes", { level: "more" }),
  },
  validate: (d) => (d.disposition === "sold" && d.until ? [{ path: "data.until", message: "Milk back in the tank has no end date." }] : []),
  summary: (e, x) => {
    const d = e.data;
    const cows = cowsLabel(e.subjects, x);
    const why = d.reason ? x.t(label(MILK_REASONS, d.reason)) : null;
    let text: string;
    if (d.disposition === "sold") text = x.t("{{cows}}: milk back in the tank", { cows });
    else if (d.disposition === "discarded") text = d.until ? x.t("{{cows}}: milk dumped until {{date}}", { cows, date: x.date(d.until) }) : x.t("{{cows}}: milk dumped", { cows });
    else text = d.until ? x.t("{{cows}}: milk kept out of the tank until {{date}}", { cows, date: x.date(d.until) }) : x.t("{{cows}}: milk kept out of the tank", { cows });
    return why ? `${text} · ${why}` : text;
  },
  agent: {
    when: "What was done with a cow's milk: dumped, kept out of the bulk tank (for example fed to calves), or put back in the tank.",
    avoid: "This records what was done, not a withdrawal rule; withdrawal holds come from health treatments. For a mastitis case use dairy.mastitis with milk dumped.",
    examples: ["dumping 0155's milk till Friday", "0142 back in the tank", "la leche de la 0160 va para los becerros"],
  },
});

// ---------------------------------------------------------------- facet

function facetBadges(s: DairyFacet, today: string): Badge[] {
  const out: Badge[] = [];
  if (milkOutActive(s, today)) out.push({ label: msg("Milk hold"), tone: "warn", icon: "warn" });
  if (s.status === "milking") {
    const dim = liveDim(s, today);
    if (dim === null) out.push({ label: msg("Milking now"), tone: "dairy", icon: "milk" });
    else if (dim <= FRESH_BADGE_DAYS) out.push({ label: msg("Fresh {{dim}} DIM", { dim }), tone: "dairy", icon: "milk" });
    else out.push({ label: msg("{{dim}} DIM", { dim }), tone: "dairy", icon: "milk" });
  } else if (s.status === "dry") out.push({ label: msg("Dry"), tone: "neutral", icon: "moon" });
  if (activeMastitis(s, today).length) out.push({ label: msg("Mastitis"), tone: "health", icon: "water" });
  return out;
}

function facetFacts(s: DairyFacet, today: string): Fact[] {
  const out: Fact[] = [];
  const n = s.lactation;
  if (s.status === "milking") {
    const dim = liveDim(s, today);
    out.push({ label: "Lactation", tone: "dairy", icon: "milk", value: n
      ? (dim !== null ? msg("Lactation {{number}}, {{dim}} days in milk", { number: n, dim }) : msg("Lactation {{number}}, milking", { number: n }))
      : (dim !== null ? msg("{{dim}} days in milk", { dim }) : msg("Milking now")) });
  } else if (s.status === "dry") {
    const days = s.dry_on ? daysBetween(s.dry_on, today) : null;
    out.push({ label: "Lactation", tone: "dairy", icon: "moon", value: days !== null
      ? (n ? msg("Dry {{days}} days · lactation {{number}}", { days, number: n }) : msg("Dry {{days}} days", { days }))
      : msg("Dry") });
  }
  const test = s.last_test;
  if (test) {
    const milk = test.milk ? `${test.milk.value} ${test.milk.unit === "l" ? "L" : test.milk.unit}` : null;
    out.push({ label: "Last test", icon: "lab", value: milk && test.scc !== undefined ? msg("{{milk}}, SCC {{scc}}k", { milk, scc: test.scc })
      : milk ? milk : test.scc !== undefined ? msg("SCC {{scc}}k", { scc: test.scc }) : msg("Components only") });
  }
  if (s.me305) out.push({ label: "305ME (calculated by Hondo)", value: `${s.me305.value} ${s.me305.unit}` });
  const out_ = milkOutActive(s, today);
  if (out_) {
    const left = out_.until ? daysBetween(today, out_.until) : null;
    out.push({ label: "Milk", tone: "warn", icon: "warn", value: left === null ? msg("Out of the tank until put back") : left === 0 ? msg("Out of the tank through today")
      : left === 1 ? msg("Out of the tank 1 more day") : msg("Out of the tank {{days}} more days", { days: left }) });
  }
  return out;
}

export const dairyFacet = defineFacet<DairyFacet>({
  id: "dairy",
  subject: "animal",
  version: 1,
  eventTypes: [...DAIRY_EVENT_TYPES, "breeding.calving"],
  reduce(events, { today }) {
    const detail = buildDairy(events, today);
    return detail ? toFacet(detail, today) : null;
  },
  badges: (s, { today }) => facetBadges(s, today),
  facts: (s, { today }) => facetFacts(s, today),
  filters: {
    status: { label: "Milking status", type: "enum", options: { milking: "Milking now", dry: "Dry", heifer: "Heifer, not fresh yet" } },
    lactation: { label: "Lactation", type: "number" },
  },
});

// ---------------------------------------------------------------- module

export default defineModule({
  id: "dairy",
  label: "Dairy",
  description: "Lactations, milk tests, fresh-cow work and dairy protocols.",
  events: [lactationStart, milkWeight, testDay, dryOff, freshCheck, mastitis, milkDisposition],
  facets: [dairyFacet],
  jobs: [
    {
      type: "dairy.fresh_round", label: "Fresh-cow checks", description: "This morning's checks of fresh cows, one per cow. Hondo starts a new round each day.",
      icon: "thermometer", tone: "dairy", workspace: "dairy", subjectKind: "animal", itemEvent: "dairy.fresh_check",
      agent: { when: "Hondo makes one each morning; record checks with dairy.fresh_check and link them to today's round." },
    },
    {
      type: "dairy.dry_off_list", label: "Dry-off list", description: "Cows due to be dried off, from their expected calving dates.",
      icon: "moon", tone: "dairy", workspace: "dairy", subjectKind: "animal", itemEvent: "dairy.dry_off",
      agent: { when: "A list of cows to dry off soon, chosen by expected calving date." },
    },
    {
      type: "dairy.test_import", label: "Test-day import", description: "A test-day file matched to cows. Rows that don't match stay here until someone sorts them out.",
      icon: "upload", tone: "dairy", workspace: "dairy", subjectKind: "animal",
      stages: [{ id: "review", label: "Check the matches" }, { id: "filed", label: "Filed" }],
      agent: { when: "Created from a test-day file in Run dairy; not proposed from a capture." },
    },
  ],
  workspaces: [{
    id: "dairy", label: "Run dairy", description: "Fresh-cow checks, dry-offs, milk tests, mastitis and milk held out of the tank.", icon: "milk", group: "care",
    keywords: ["dairy", "milk", "milking", "lactation", "fresh cow", "fresh check", "dry off", "test day", "DHIA", "SCC", "somatic cells", "mastitis", "bulk tank", "parlor", "305ME"],
    keywordsEs: ["lechería", "lecheria", "ordeña", "ordeño", "ordeno", "leche", "lactancia", "secado", "secar", "recién parida", "recien parida", "fresca", "mastitis", "prueba de leche", "control lechero", "células somáticas", "celulas somaticas", "tanque"],
    presets: ["dairy"],
  }],
  attributes: {
    animal: {
      barn_name: f.text("Barn name or number", { level: "more", help: "The name or number used in the parlor and on test-day sheets." }),
    },
  },
  settings: {
    fresh_days: f.int("Fresh checks through day", { min: 1, max: 60, help: "Cows get a fresh-cow check every day until this many days in milk." }),
    dry_period: f.int("Dry period", { suffix: "days", min: 20, max: 120, help: "How many days before the expected calving date a cow is dried off." }),
    scc_limit: f.int("High SCC from", { suffix: "×1,000 cells/mL", min: 50, max: 2000, help: "Cows at or over this on a test show up on the morning list." }),
  },
  // Built from maps in summaries and screens, so the catalog check can see them.
  messages: [
    "mild", "moderate", "severe", "from the lab", "counted from records", "Fever", "Discharge", "DA ping",
    "No dry-off was recorded before the next lactation.", "More than one dry-off is recorded in this lactation.", "Milk was recorded after this lactation's dry-off.",
    "The fresh date isn't known.", "Fresh date estimated from the lab's days in milk.",
  ],
});

export { freshFlags };
